import React, { useState, useEffect, useCallback } from 'react';
import './TemplatesPage.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSync, faCopy, faEye, faCheckCircle, faClock, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import CreateTemplateModal from './TemplateCreateModal';
import WhatsAppPreview from './WhatsAppPreview';
import OutletBadge from './OutletBadge';

const API_URL = process.env.REACT_APP_API_URL;
const BASE_URL = `${API_URL}/whatsapp`;

const TemplatesPage = ({ token, business_id, businessName }) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('ALL');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalData, setModalData] = useState(null); // For duplication/view modes
    const [previewTemplate, setPreviewTemplate] = useState(null); // For view-only modal

    const fetchTemplates = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch(`${BASE_URL}/get_templates`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            // Handle various response structures
            let data = [];
            if (Array.isArray(result)) {
                data = result;
            } else if (result.data && Array.isArray(result.data)) {
                data = result.data;
            } else if (result.templates && Array.isArray(result.templates)) {
                data = result.templates;
            }

            // Sort by created at if available, otherwise by name
            // Assuming 'created_at' or similar might exist, else ID matches time somewhat
            data.sort((a, b) => {
                const timeA = a.created_at || 0;
                const timeB = b.created_at || 0;
                return new Date(timeB) - new Date(timeA);
            });

            setTemplates(data);
        } catch (err) {
            console.error('Error fetching templates:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (token) {
            fetchTemplates();
        }
    }, [token, fetchTemplates]);

    const mapTemplateToData = (template) => {
        const headerComp = template.components.find(c => c.type === 'HEADER');
        const bodyComp = template.components.find(c => c.type === 'BODY');
        const footerComp = template.components.find(c => c.type === 'FOOTER');
        const buttonsComp = template.components.find(c => c.type === 'BUTTONS');

        return {
            basicInfo: {
                name: template.name,
                category: template.category,
                language: template.language
            },
            header: {
                type: headerComp ? headerComp.format : 'NONE',
                text: headerComp && headerComp.format === 'TEXT' ? headerComp.text : '',
                mediaFile: null, // Cannot pre-fill file object
                mediaHandle: headerComp?.example?.header_handle?.[0] || null,
                mediaPreviewUrl: headerComp?.example?.header_handle?.[0] || null // Handle might be url? Unlikely but assuming yes for preview
            },
            body: bodyComp ? bodyComp.text : '',
            footer: footerComp ? footerComp.text : '',
            buttons: buttonsComp ? buttonsComp.buttons.map(b => ({
                type: b.type,
                text: b.text,
                url: b.url,
                phone_number: b.phone_number
            })) : []
        };
    };

    const handleDuplicate = (template) => {
        const mappedData = mapTemplateToData(template);
        setModalData(mappedData);
        setIsModalOpen(true);
    };

    const handleView = (template) => {
        const mappedData = mapTemplateToData(template);
        setPreviewTemplate(mappedData);
    };

    const handleCreateNew = () => {
        setModalData(null);
        setIsModalOpen(true);
    };

    const filteredTemplates = templates.filter(t => {
        if (activeTab === 'ALL') return true;
        if (activeTab === 'APPROVED') return t.status === 'APPROVED';
        if (activeTab === 'PENDING') return ['PENDING', 'IN_REVIEW'].includes(t.status);
        return false;
    });

    const getStatusIcon = (status) => {
        if (status === 'APPROVED') return faCheckCircle;
        if (status === 'REJECTED') return faTimesCircle;
        return faClock;
    };

    const getStatusClass = (status) => {
        if (status === 'APPROVED') return 'wt-status-approved';
        if (status === 'REJECTED') return 'wt-status-rejected';
        return 'wt-status-pending';
    };

    // Helper to get formatted date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    return (
        <div className="wt-page-container">
            <div className="wt-header">
                <div className="wt-title-outlet-group">
                    <div className="wt-title">WhatsApp Templates</div>
                    <OutletBadge businessName={businessName} businessId={business_id} pageTitle="WhatsApp Templates" />
                </div>
                <div className="wt-header-actions">
                    <button className="wt-btn wt-btn-secondary" onClick={fetchTemplates} disabled={loading}>
                        <FontAwesomeIcon icon={faSync} spin={loading} />
                        {loading ? 'Syncing...' : 'Resync'}
                    </button>
                    <button className="wt-btn wt-btn-primary" onClick={handleCreateNew}>
                        <FontAwesomeIcon icon={faPlus} />
                        Create Template
                    </button>
                </div>
            </div>

            <div className="wt-tabs">
                <button
                    className={`wt-tab ${activeTab === 'ALL' ? 'active' : ''}`}
                    onClick={() => setActiveTab('ALL')}
                >
                    All Templates
                    <span className="wt-badge-count">{templates.length}</span>
                </button>
                <button
                    className={`wt-tab ${activeTab === 'APPROVED' ? 'active' : ''}`}
                    onClick={() => setActiveTab('APPROVED')}
                >
                    Approved
                    <span className="wt-badge-count">{templates.filter(t => t.status === 'APPROVED').length}</span>
                </button>
                <button
                    className={`wt-tab ${activeTab === 'PENDING' ? 'active' : ''}`}
                    onClick={() => setActiveTab('PENDING')}
                >
                    Pending Approval
                    <span className="wt-badge-count">{templates.filter(t => ['PENDING', 'IN_REVIEW'].includes(t.status)).length}</span>
                </button>
            </div>

            {loading && templates.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>Loading templates...</div>
            ) : (
                <div className="wt-grid">
                    {filteredTemplates.map(template => (
                        <div className="wt-card" key={template.id}>
                            <div className="wt-card-header">
                                <div className="wt-template-name">{template.name}</div>
                                <div className={`wt-status-badge ${getStatusClass(template.status)}`}>
                                    <FontAwesomeIcon icon={getStatusIcon(template.status)} style={{ marginRight: '4px' }} />
                                    {template.status}
                                </div>
                            </div>
                            <div className="wt-card-body">
                                <div className="wt-info-row">
                                    <span className="wt-info-label">Category</span>
                                    <span className="wt-info-value">{template.category}</span>
                                </div>
                                {/* Language row removed */}
                                <div className="wt-info-row">
                                    <span className="wt-info-label">Type</span>
                                    <span className="wt-info-value">
                                        {/* Infer type from components */}
                                        {template.components.some(c => c.type === 'CAROUSEL') ? 'Carousel' :
                                            template.components.some(c => c.type === 'HEADER' && c.format !== 'TEXT') ? 'Media' : 'Standard'}
                                    </span>
                                </div>
                                {/* Created Date if available */}
                                {template.created_at && (
                                    <div className="wt-info-row">
                                        <span className="wt-info-label">Created</span>
                                        <span className="wt-info-value">{formatDate(template.created_at)}</span>
                                    </div>
                                )}
                            </div>
                            <div className="wt-card-footer">
                                <button className="wt-action-btn" onClick={() => handleDuplicate(template)}>
                                    <FontAwesomeIcon icon={faCopy} /> Duplicate
                                </button>
                                <button className="wt-action-btn" onClick={() => handleView(template)}>
                                    <FontAwesomeIcon icon={faEye} /> View
                                </button>
                            </div>
                        </div>
                    ))}
                    {filteredTemplates.length === 0 && !loading && (
                        <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>
                            No templates found.
                        </div>
                    )}
                </div>
            )}

            <CreateTemplateModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                token={token}
                onSuccess={fetchTemplates}
                initialData={modalData}
            />

            {/* View Only Modal */}
            {previewTemplate && (
                <div className="wt-modal-overlay" onClick={() => setPreviewTemplate(null)}>
                    <div className="wt-modal-content" style={{ maxWidth: '450px', height: 'auto', maxHeight: '90vh', width: '95vw', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
                        <div className="wt-modal-header">
                            <div className="wt-modal-title">Preview: {previewTemplate.basicInfo.name}</div>
                            <button className="wt-close-btn" onClick={() => setPreviewTemplate(null)}>×</button>
                        </div>
                        <div style={{ padding: '24px', overflowY: 'auto', display: 'flex', justifyContent: 'center', background: '#f0f2f5' }}>
                            <WhatsAppPreview
                                header={previewTemplate.header}
                                body={previewTemplate.body}
                                footer={previewTemplate.footer}
                                buttons={previewTemplate.buttons}
                            />
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default TemplatesPage;
